import { A } from '@ember/array';
import { filter } from '@ember/object/computed';
import { computed } from '@ember/object';
import { attr } from '@ember-data/model';
import ShModel from './sh-model';
import { memberAction } from "ember-api-actions";
import { SH_BILLABLE_LINE_ITEM } from "./sh-billable-line-item";

/**
 * @type {Object}   Potential types for the billable record objects
 */
const TYPES = {
  BATCH: 'batch',
  PROJECT: 'project',
  PROJECT_ADVANCE: 'project_advance',
};

const AGREEMENT = {
  BUYER_APPROVED: 1.0,
  SELLER_APPROVED: 2.0,
  AGREED: 3.0,
};

export const BILLABLE_RECORD = {
  TYPES: TYPES,
  AGREEMENT: AGREEMENT,
};

const BillableRecord = ShModel.extend({
  amount: attr('number'),
  approved: attr('boolean'),
  agreement: attr('number'),
  expedite: attr('boolean'),
  canExpedite: attr('boolean'),
  canCancelExpedite: attr('boolean'),
  paidAmount: attr('number'),
  feesAmount: attr('number'),
  nonFeesAmount: attr('number'),
  createdAt: attr('date'),
  dueAt: attr('date'),
  finalAt: attr('date'),
  updatedAt: attr('date'),
  paidAt: attr('date'),
  entityId: attr('number'),
  entityType: attr('string'),
  description: attr('string'),
  status: attr(),
  fileName: attr('string'),
  type: attr('string'),

  /**
   * Abstract method. Children MUST implement.
   */
  //shBillableLineItems: readOnly('shBillableLineItems'),

  /** @type {Boolean} */
  isDue: computed('dueAt', function () {
    return !!this.get('dueAt');
  }),

  /** @type {Boolean} */
  isFinal: computed('finalAt', function () {
    return this.get('finalAt') != null;
  }),

  /** @type {Boolean} */
  isPaid: computed('paidAt', function () {
    return this.get('paidAt') != null;
  }),

  /**
   * @type {Number} Base amount on billable
   */
  baseAmount: computed(
    'shBillableLineItems.@each.{amount,type}',
    function () {
      return this.get('shBillableLineItems').reduce((carry, shBillableLineItem) => {
        if (shBillableLineItem.get('type') === SH_BILLABLE_LINE_ITEM.TYPES.BASE) {
          carry += shBillableLineItem.get('amount');
        }
        return carry;
      }, 0.0);
    }
  ),

  /**
   * @type {String} Base amount on billable
   */
  baseDescription: computed(
    'shBillableLineItems.@each.{description,type}',
    function () {
      const baseLineItem = this.get('shBillableLineItems').find((shBillableLineItem) => {
        return shBillableLineItem.get('type') === SH_BILLABLE_LINE_ITEM.TYPES.BASE;
      });

      if (baseLineItem) {
        return baseLineItem.get('description');
      }

      return undefined;
    }
  ),

  /**
   * @type {Number} Base amount on billable
   */
  cancellationAmount: computed(
    'shBillableLineItems.@each.{amount,type}',
    function () {
      return this.get('shBillableLineItems').reduce((carry, shBillableLineItem) => {
        if (shBillableLineItem.get('type') === SH_BILLABLE_LINE_ITEM.TYPES.CANCELLATION) {
          carry += shBillableLineItem.get('amount');
        }
        return carry;
      }, 0.0);
    }
  ),

  /**
   * @type {Number}     The total amount of this billable
   */
  totalAmount: computed(
    'shBillableLineItems.@each.amount',
    function() {
      return this.get('shBillableLineItems').reduce((carry, shBillableLineItem) => {
        return carry + shBillableLineItem.get('amount');
      }, 0);
    }
  ),

  /**
   * Array of expenses
   *
   * @type {Ember.A<BillableRecord>}
   */
  expenseShBillableLineItems: filter(
    'shBillableLineItems.@each.type',
    function(shBillableLineItem, index, array) {
      return shBillableLineItem.get('type') === SH_BILLABLE_LINE_ITEM.TYPES.EXPENSE;
    }
  ),

  /**
   * @type {Number}     The total value of all expenses.
   */
  expenseTotalAmount: computed(
    'expenseShBillableLineItems.@each.amount',
    function() {
      return this.get('expenseShBillableLineItems').reduce((carry, shBillableLineItem) => carry + shBillableLineItem.get('amount'), 0);
    }
  ),

  /**
   * Array of fees
   *
   * @type {Ember.A<BillableRecord>}
   */
  feeShBillableLineItems: filter(
    'shBillableLineItems.@each.type',
    function(shBillableLineItem, index, array) {
      return SH_BILLABLE_LINE_ITEM.LINE_ITEM_FEE_TYPES.includes(shBillableLineItem.get('type'));
    }
  ),

  /**
   * @type {Ember.A<>}     The total value of all expenses.
   */
  consolidatedFees: computed(
    'shBillableLineItems.@each.{amount,type}',
    function() {
      const feesArray = A();
      const shBillableLineItems = this.get('shBillableLineItems');

      SH_BILLABLE_LINE_ITEM.LINE_ITEM_FEE_TYPES.forEach((feeType) => {
        const amount = shBillableLineItems.reduce((carry, shBillableLineItem) => {
          if (shBillableLineItem.get('type') === feeType) {
            return carry + shBillableLineItem.get('amount');
          }
          return carry;
        }, 0.0);

        feesArray.pushObject({
          amount: amount,
          description: SH_BILLABLE_LINE_ITEM.TYPE_DESCRIPTIONS[feeType],
          type: feeType
        });
      });

      return feesArray;
    }
  ),

  /**
   * @type {Number}     The total value of all fees.
   */
  feeTotalAmount: computed(
    'feeShBillableLineItems.@each.amount',
    function() {
      return this.get('feeShBillableLineItems').reduce((carry, shBillableLineItem) => carry + shBillableLineItem.get('amount'), 0);
    }
  ),

  /**
   * @type {Number} The unpaid portion of the billable
   */
  unpaidAmount: computed(
    'amount',
    'paidAmount',
    function () {
      return this.get('amount') - this.get('paidAmount');
    }
  ),

  /************ LEGACY API CALLS ************/

  pay: memberAction({
    path: 'pay',
    type: 'put',
    urlType: 'updateRecord'
  }),
  send: memberAction({
    path: 'send',
    type: 'put',
    urlType: 'updateRecord'
  }),
  makePDF: memberAction({
    path: 'pdf',
    type: 'get',
    urlType: 'updateRecord'
  })
});

export default BillableRecord;
